// @flow
import React, {useEffect, useState, useCallback, useRef} from 'react';
import SizeGuideSection from './SizeGuideSection/SizeGuideSection';
import EditNewSection from './EditNewSection/EditNewSection';
import {
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {useFieldArray, useFormContext} from 'react-hook-form';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import AddIcon from '@material-ui/icons/Add';
import {colors} from '../../../../../assets/styles/colors';
import {useProductFormContext} from '../../../context/ProductForm';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {usePromise} from '../../../../../utils/request';

import {getProductSectionsService} from '../../../promises';

import type {ProductDetail, ProductSection} from '../../../../../entities';
import {getImageUrl} from '../../../../../utils/image_provider';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {useStoreBusinessModel} from '../../../../store/utils';

type Props = {
  product?: ProductDetail,
};

export type Movement = {
  init?: number,
  end?: number,
};

const useStyles = makeStyles(theme => ({
  root: {},
  addSectionBtn: {
    border: `1px solid ${colors.grey400}`,
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addSectionBtnContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    fontSize: 14,
    padding: theme.spacing(1.5, 0),
  },
  text: {
    fontSize: 14,
  },
}));

export default function Sections({product}: Props) {
  const classes = useStyles();
  const {control} = useFormContext();

  const itemDraggedRef = useRef();

  const [itemDraggedId, setItemDraggedId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [movement, setMovement] = useState<Movement | null>(null);

  const hasProductSectionsFeature = useIsFeatureInTier('PRODUCT_SECTIONS');
  const storeBusinessModel = useStoreBusinessModel();

  const {status, reqHandler, data: productSections} = usePromise<
    ProductSection[]
  >(getProductSectionsService, true);

  const {setSectionImages} = useProductFormContext();

  const {fields, append, prepend, remove, move} = useFieldArray({
    control,
    name: 'productSections',
  });

  const handleAddSection = () => {
    setSectionImages(ps => [
      ...ps,
      {file: null, preview: null, imageId: undefined},
    ]);
    append({
      title: '',
      enabled: true,
      content: '',
      productSectionImageLayout: null,
    });
  };

  const handleRemoveSection = (idx: number) => {
    remove(idx);
    setSectionImages(ps => {
      const aux = [...ps];
      aux.splice(idx, 1);
      return aux;
    });
  };

  useEffect(() => {
    if (hasProductSectionsFeature && product) {
      reqHandler(product.id);
    }
  }, [hasProductSectionsFeature, product, reqHandler]);

  useEffect(() => {
    if (productSections) {
      setSectionImages(
        productSections.map(el => ({
          preview: el.imageId ? getImageUrl(el.imageId) : null,
          file: null,
          imageId: el.imageId,
          productSectionImageLayout: el.productSectionImageLayout,
        }))
      );

      const parsedSections = productSections.map(
        ({content, enabled, productSectionImageLayout, title}) => ({
          title,
          productSectionImageLayout,
          content,
          enabled,
        })
      );
      prepend(parsedSections);
    }
  }, [prepend, productSections, setSectionImages]);

  const getIndexOfElement = (arr = [], id) => arr.findIndex(el => el.id === id);

  const handleDragStart = (e, startItemId) => {
    const dragImg = document.createElement('img');
    dragImg.src =
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    e.dataTransfer.setDragImage(dragImg, 0, 0);
    setIsDragging(true);
    setItemDraggedId(startItemId);
    setMovement({init: getIndexOfElement(fields, startItemId)});
    itemDraggedRef.current = e.target;
  };

  const existsItemDragged = itemDraggedId !== null;

  const handleDragEnter = useCallback(
    (e, enterItemId) => {
      if (itemDraggedRef.current !== e.target) {
        if (existsItemDragged) {
          const pos = getIndexOfElement(fields, enterItemId);
          setMovement((prev: Movement) => ({
            ...prev,
            end: pos,
          }));
        }
      }
    },
    [fields, existsItemDragged]
  );

  const init = movement?.init;
  const end = movement?.end;

  useEffect(() => {
    if (typeof init === 'number' && typeof end === 'number') {
      move(init, end);
    }
  }, [end, init, move]);

  const onDragEnd = useCallback(() => {
    if (existsItemDragged && itemDraggedRef.current && movement) {
      setIsDragging(false);
      setItemDraggedId(null);
      setMovement(null);
      //$FlowFixMe
      itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      itemDraggedRef.current = null;
    }
  }, [existsItemDragged, movement]);

  useEffect(() => {
    if (isDragging && itemDraggedRef?.current) {
      itemDraggedRef.current.addEventListener('dragend', onDragEnd);

      return () => {
        itemDraggedRef &&
          itemDraggedRef.current &&
          itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isDragging, onDragEnd]);

  return (
    <div>
      <Grid container spacing={2}>
        {storeBusinessModel === 'PRODUCT' && (
          <Grid item xs={12}>
            <SizeGuideSection />
          </Grid>
        )}
        {status === 'LOADING' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </div>
        )}

        {fields.map((el, idx: number) => (
          <Grid key={el.id} item xs={12}>
            <EditNewSection
              key={el.id}
              section={el}
              idx={idx}
              onRemove={() => handleRemoveSection(idx)}
              handleDragStart={handleDragStart}
              handleDragEnter={handleDragEnter}
              isDragging={isDragging}
              disabled={false}
              dragItemId={itemDraggedId ?? '-1'}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <CustomButton
            fullWidth
            className={classes.addSectionBtn}
            onClick={handleAddSection}
            // disabled={!hasProductSectionsFeature}
          >
            <div className={classes.addSectionBtnContent}>
              <AddIcon color="primary" />
              <Typography color="primary" className={classes.text}>
                Agregar otra sección
                {!hasProductSectionsFeature && <PremiumFeatureBadge />}
              </Typography>
            </div>
          </CustomButton>
        </Grid>
      </Grid>
    </div>
  );
}
